import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useSetCustomDomain } from "@/queries/organization-queries";
import { isValidDomain, normalizeDomain } from "@/utils/url-validation";
import { formatError } from "@/utils/errors";
import { TrackLocations } from "@/hooks/use-segment";
import { type FC, useState, useEffect } from "react";
import type { CustomDomain } from "gitpod-next-api/gitpod/v1/organization_pb";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";

type SetupCustomDomainDialogProps = {
    organizationId: string;
    isOpen: boolean;
    onClose: () => void;
    isEditing?: boolean;
    existingDomain?: CustomDomain;
};

function normalizeAwsAccountId(accountId: string): string {
    return accountId.replace(/[-\s]/g, "");
}

function isValidAwsAccountId(accountId: string): boolean {
    if (!accountId || accountId.trim().length === 0) {
        return false;
    }
    const normalized = normalizeAwsAccountId(accountId);
    // Must be exactly 12 digits
    return /^\d{12}$/.test(normalized);
}

function validateDomain(value: string): string {
    if (!value.trim()) {
        return "Domain is required";
    }
    // Normalize domain before validation (removes protocol if present)
    const normalized = normalizeDomain(value);
    if (!isValidDomain(normalized)) {
        return "Please enter a valid domain (e.g., company.com)";
    }
    return "";
}

function validateAwsAccountId(value: string): string {
    if (!value.trim()) {
        return "AWS Account ID is required";
    }
    if (!isValidAwsAccountId(value)) {
        return "AWS Account ID must be exactly 12 digits";
    }
    return "";
}

export const SetupCustomDomainDialog: FC<SetupCustomDomainDialogProps> = ({
    organizationId,
    isOpen,
    onClose,
    isEditing = false,
    existingDomain,
}) => {
    const { toast } = useToast();
    const setCustomDomain = useSetCustomDomain();

    const [domain, setDomain] = useState("");
    const [awsAccountId, setAwsAccountId] = useState("");
    const [domainError, setDomainError] = useState("");
    const [awsAccountIdError, setAwsAccountIdError] = useState("");
    const [domainTouched, setDomainTouched] = useState(false);
    const [awsAccountIdTouched, setAwsAccountIdTouched] = useState(false);

    // Pre-fill form when editing
    useEffect(() => {
        if (isEditing && existingDomain) {
            setDomain(existingDomain.domainName || "");
            setAwsAccountId(existingDomain.awsAccountId || "");
        } else {
            setDomain("");
            setAwsAccountId("");
        }
        setDomainError("");
        setAwsAccountIdError("");
        setDomainTouched(false);
        setAwsAccountIdTouched(false);
    }, [isEditing, existingDomain, isOpen]);

    const handleDomainBlur = () => {
        setDomainTouched(true);
        setDomainError(validateDomain(domain));
    };

    const handleAwsAccountIdBlur = () => {
        setAwsAccountIdTouched(true);
        setAwsAccountIdError(validateAwsAccountId(awsAccountId));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate all fields
        const domainErr = validateDomain(domain);
        const awsAccountIdErr = validateAwsAccountId(awsAccountId);

        setDomainError(domainErr);
        setAwsAccountIdError(awsAccountIdErr);
        setDomainTouched(true);
        setAwsAccountIdTouched(true);

        if (domainErr || awsAccountIdErr) {
            return;
        }

        try {
            await setCustomDomain.mutateAsync({
                organizationId,
                domainName: normalizeDomain(domain),
                awsAccountId: normalizeAwsAccountId(awsAccountId),
            });

            toast({
                title: isEditing ? "Custom domain updated successfully" : "Custom domain configured successfully",
            });

            onClose();
        } catch (error) {
            toast({
                title: isEditing ? "Failed to update custom domain" : "Failed to configure custom domain",
                description: formatError(error),
            });
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent data-track-location={TrackLocations.SetupCustomDomainModal}>
                <DialogHeader>
                    <DialogTitle>{isEditing ? "Edit custom domain" : "Set up custom domain"}</DialogTitle>
                    <DialogDescription>
                        Register your custom domain to access Ona through your own branded domain. After registration,
                        you&apos;ll need to configure AWS infrastructure (Load Balancer, VPC Endpoint, DNS) to complete the
                        setup.{" "}
                        <ExternalLink href="https://ona.com/docs/ona/custom-domain" className="text-sm">
                            View setup documentation
                        </ExternalLink>
                    </DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="aws-account-id">AWS Account ID</Label>
                            <Input
                                id="aws-account-id"
                                type="text"
                                placeholder="123456789012"
                                value={awsAccountId}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setAwsAccountId(e.target.value);
                                    if (awsAccountIdTouched) {
                                        setAwsAccountIdError(validateAwsAccountId(e.target.value));
                                    }
                                }}
                                onBlur={handleAwsAccountIdBlur}
                                aria-required="true"
                                aria-invalid={!!awsAccountIdError}
                                aria-describedby={awsAccountIdError ? "aws-account-id-error" : "aws-account-id-help"}
                            />
                            {awsAccountIdError && awsAccountIdTouched ? (
                                <p id="aws-account-id-error" className="text-sm text-content-destructive" role="alert">
                                    {awsAccountIdError}
                                </p>
                            ) : (
                                <p id="aws-account-id-help" className="text-sm text-content-secondary">
                                    The 12-digit AWS account ID where you will configure the Load Balancer and VPC
                                    Endpoint. Find it in the AWS Management Console under My Account.
                                </p>
                            )}
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label htmlFor="domain">Domain name</Label>
                            <Input
                                id="domain"
                                type="text"
                                placeholder="ona.example.com"
                                value={domain}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setDomain(e.target.value);
                                    if (domainTouched) {
                                        setDomainError(validateDomain(e.target.value));
                                    }
                                }}
                                onBlur={handleDomainBlur}
                                aria-required="true"
                                aria-invalid={!!domainError}
                                aria-describedby={domainError ? "domain-error" : "domain-help"}
                            />
                            {domainError && domainTouched ? (
                                <p id="domain-error" className="text-sm text-content-destructive" role="alert">
                                    {domainError}
                                </p>
                            ) : (
                                <>
                                    <p id="domain-help" className="text-sm text-content-secondary">
                                        Enter domain without https:// (e.g., company.com or login.company.com)
                                    </p>
                                    {domain && normalizeDomain(domain) && (
                                        <p className="text-xs text-content-secondary">
                                            Will be accessible at: <span className="font-medium">https://{normalizeDomain(domain)}</span>
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </form>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={setCustomDomain.isPending}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleSubmit}
                        loading={setCustomDomain.isPending}
                        data-tracking-id="submit-custom-domain"
                    >
                        {isEditing ? "Update" : "Set Up"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
